import { useEffect, useRef, useState } from 'react';

import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import { Element } from 'common/types/tracking';
import type { RelatedSearchPlacement } from '../components/RelatedSearch';

interface RelatedSearchItem {
  query: string;
}

const enum EventName {
  View = 'related-search_view',
  Click = 'related-search_click',
}

const DEBOUNCE_TIMEOUT = 300;

export const useRelatedSearchTracking = (
  items: RelatedSearchItem[],
  placement: RelatedSearchPlacement
) => {
  const [displayedIndices, setDisplayedIndices] = useState<number[]>([]);
  const trackedIndices = useRef<Set<number>>(new Set());

  const handleObserve = (index: number) => {
    if (trackedIndices.current.has(index)) return;
    setDisplayedIndices(prev => (prev.includes(index) ? prev : [...prev, index]));
  };

  const handleClick = (item: RelatedSearchItem, index: number) => {
    dispatchTrackingEventGA4({
      event: EventName.Click,
      element: Element.Link,
      list_length: items.length,
      list_position: index,
      label: item.query,
      placement,
    });
  };

  useEffect(() => {
    if (!displayedIndices.length) return;

    const timer = setTimeout(() => {
      dispatchTrackingEventGA4({
        event: EventName.View,
        list_length: items.length,
        placement,
        items: displayedIndices.map(i => ({
          list_position: i,
          list_item: items[i].query,
        })),
      });
      displayedIndices.forEach(i => trackedIndices.current.add(i));
      setDisplayedIndices([]);
    }, DEBOUNCE_TIMEOUT);

    return () => clearTimeout(timer);
  }, [displayedIndices, items, placement]);

  return { handleObserve, handleClick };
};
